/*
 * The pet form's values and what a save sends (SF 34, 65–67; phase 4). One
 * pure module for the one pet sheet and the first-pet steps, so every way of
 * saving a pet sends the same thing:
 *   - the sex as the API numbers it (utils/petGender, H2);
 *   - the behaviour level as its number (G6);
 *   - no birthday as null, never "" (the API refuses "");
 *   - how much of the birthday is known, beside the date: an exact day, only
 *     the month, or an age, which counts as the month it's typed in
 *     (`birthdayPrecision`; mpgadmin D102, this repo's D25);
 *   - the type as saved on an edit: the API never changes it from here
 *     (mpgadmin PetService ignores it; staff change it), so the form doesn't
 *     offer it.
 * Days are plain "2026-10-12" strings on the salon's calendar; "today" is
 * passed in.
 */
import type { Pet } from '../services/api';
import { behaviourLevel, behaviourName } from './petBehaviour';
import { genderFromApi, genderToApi, type PetGenderName } from './petGender';
import { NOTE_MAX } from './importantInfo';

export type PetKind = 'dog' | 'cat';

export interface PetForm {
  name: string;
  type: PetKind | '';
  gender: PetGenderName;
  breed: string;
  breedId?: number;
  /** "Birthday", "only the month", or "an age instead". */
  birthdayMode: 'date' | 'month' | 'age';
  /** "2022-03-12", or "". */
  birthday: string;
  /** "2023-09" when only the month is known, or "". */
  birthdayMonth: string;
  /** Whole years, or null. */
  ageYears: number | null;
  /** As typed, in kg: "28", "7.5", or "". */
  weightKg: string;
  /** None, Mild, Moderate, Severe, or "" (none chosen). */
  behaviour: string;
  info: string;
}

export const emptyPetForm = (type: PetKind | '' = ''): PetForm => ({
  name: '',
  type,
  gender: '',
  breed: '',
  breedId: undefined,
  birthdayMode: 'date',
  birthday: '',
  birthdayMonth: '',
  ageYears: null,
  weightKg: '',
  behaviour: '',
  info: ''
});

/** "28kg", "28 kg", 28 → "28"; nothing → "". */
export const weightText = (weight?: string | number | null): string => {
  if (weight === null || weight === undefined || weight === '') return '';
  const n = typeof weight === 'number' ? weight : parseFloat(String(weight).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? String(n) : '';
};

/** The form for a pet as the app holds it. */
export const petFormFromPet = (pet: Pet): PetForm => {
  const day = (pet.dob || '').slice(0, 10);
  // A month-only birthday opens on its month, so a re-save doesn't make it an exact day.
  const monthOnly = pet.birthdayPrecision === 'month' && /^\d{4}-\d{2}-\d{2}$/.test(day);
  return {
    name: pet.name || '',
    type: pet.type === 'cat' ? 'cat' : pet.type === 'dog' ? 'dog' : '',
    gender: pet.gender === 'Male' || pet.gender === 'Female' ? pet.gender : '',
    breed: pet.breed || '',
    breedId: pet.breedId || undefined,
    birthdayMode: monthOnly ? 'month' : 'date',
    birthday: !monthOnly && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : '',
    birthdayMonth: monthOnly ? day.slice(0, 7) : '',
    ageYears: null,
    weightKg: weightText(pet.weight),
    behaviour: behaviourName(pet.aggression),
    info: pet.info || ''
  };
};

/** The weight as typed, in kg: null for none, NaN for something that isn't one. */
export const parseWeightKg = (text: string): number | null => {
  const t = text.trim().replace(',', '.');
  if (!t) return null;
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return NaN;
  const n = Number(t);
  return n > 0 && n <= 150 ? n : NaN;
};

export const MAX_AGE_YEARS = 25;

/**
 * The date of birth a save sends: the birthday; the month's 1st when only the
 * month is known; for an age, the 1st of this month that many years ago, so
 * the pet's birthday month is the one the age was given in (Lena, 20 Oct
 * 2026), as the admin's Age box does; or null.
 */
export const dateOfBirthOf = (form: PetForm, today: string): string | null => {
  if (form.birthdayMode === 'date') return /^\d{4}-\d{2}-\d{2}$/.test(form.birthday) ? form.birthday : null;
  if (form.birthdayMode === 'month') return /^\d{4}-\d{2}$/.test(form.birthdayMonth) ? `${form.birthdayMonth}-01` : null;
  if (form.ageYears === null || form.ageYears < 0) return null;
  return ageDob(form.ageYears, today);
};

/** An age as the API keeps it: the 1st of this month, that many years ago. */
export const ageDob = (years: number, today: string): string =>
  `${Number(today.slice(0, 4)) - years}-${today.slice(5, 7)}-01`;

export type BirthdayPrecision = 'day' | 'month' | 'none';

/** How much of the birthday a save knows, sent beside the date (mpgadmin D102): the API then never guesses. */
export const birthdayPrecisionOf = (form: PetForm, today: string): BirthdayPrecision =>
  dateOfBirthOf(form, today) === null ? 'none' : form.birthdayMode === 'date' ? 'day' : 'month';

export type PetFormField = 'name' | 'type' | 'gender' | 'weight' | 'birthday' | 'info';

/** What stops a save, field by field, in words. Empty when it can be saved. */
export const petFormProblems = (form: PetForm, today: string): Partial<Record<PetFormField, string>> => {
  const out: Partial<Record<PetFormField, string>> = {};
  if (!form.name.trim()) out.name = 'Add a name';
  else if (form.name.trim().length > 50) out.name = 'Keep the name to 50 characters';
  if (!form.type) out.type = 'Choose dog or cat';
  if (!form.gender) out.gender = 'Choose male or female';
  if (Number.isNaN(parseWeightKg(form.weightKg))) out.weight = 'Type a weight in kg, like 12 or 7.5';
  if (form.birthdayMode === 'date' && form.birthday && form.birthday > today) out.birthday = 'A birthday can’t be in the future';
  if (form.birthdayMode === 'month' && form.birthdayMonth && form.birthdayMonth > today.slice(0, 7))
    out.birthday = 'A birthday can’t be in the future';
  if (form.info.length > NOTE_MAX) out.info = `Keep it to ${NOTE_MAX} characters`;
  return out;
};

export interface PetBody {
  id?: number;
  name: string;
  petType: 1 | 2;
  petGender: 0 | 1 | null;
  breedId?: number;
  dateOfBirth: string | null;
  birthdayPrecision: BirthdayPrecision;
  weight: number;
  petAggressiveBehavior: number | null;
  note: string;
}

const petTypeNumber = (type: PetKind | '' | undefined): 1 | 2 => (type === 'cat' ? 1 : 2);

/**
 * What a create or an edit sends to /api/pet. On an edit the type is the one
 * on record (`savedType`); no weight is 0, which the app reads as none.
 */
export const petBody = (form: PetForm, today: string, edit?: { id: number; savedType?: PetKind }): PetBody => {
  const petType = petTypeNumber(edit ? edit.savedType ?? form.type : form.type);
  const weight = parseWeightKg(form.weightKg);
  return {
    ...(edit ? { id: edit.id } : {}),
    name: form.name.trim(),
    petType,
    petGender: genderToApi(form.gender),
    // The breed by its id only, never a `breed` object: the API made one into
    // a new breed to insert and every create with a breed failed (G6).
    ...(form.breedId ? { breedId: form.breedId } : {}),
    dateOfBirth: dateOfBirthOf(form, today),
    birthdayPrecision: birthdayPrecisionOf(form, today),
    weight: weight && Number.isFinite(weight) ? weight : 0,
    petAggressiveBehavior: behaviourLevel(form.behaviour),
    note: form.info.trim()
  };
};

/** A pet made before signing in, as the local pet store keeps it. */
export const localPetOf = (form: PetForm, today: string): Omit<Pet, 'id'> => {
  const dob = dateOfBirthOf(form, today);
  const weight = parseWeightKg(form.weightKg);
  return {
    name: form.name.trim(),
    type: form.type === 'cat' ? 'cat' : 'dog',
    breed: form.breed || undefined,
    breedId: form.breedId,
    gender: form.gender,
    weight: weight && Number.isFinite(weight) ? `${weight}kg` : '',
    age: dob ? ageOn(dob, today) : undefined,
    dob: dob ?? '',
    birthdayPrecision: birthdayPrecisionOf(form, today),
    aggression: behaviourName(form.behaviour),
    info: form.info.trim()
  };
};

/** The pet as the app holds it after the server answered a save. */
export const petFromApi = (api: any, today: string): Pet => ({
  id: api.id,
  type: api.petType === 1 ? 'cat' : 'dog',
  name: api.name,
  breed: api.breed?.name || '',
  breedId: api.breedId || api.breed?.id || undefined,
  gender: genderFromApi(api.petGender),
  weight: api.weight > 0 ? `${api.weight}kg` : '',
  age: api.dateOfBirth ? ageOn(String(api.dateOfBirth).slice(0, 10), today) : undefined,
  dob: api.dateOfBirth || '',
  birthdayPrecision: precisionFromApi(api.birthdayPrecision),
  aggression: behaviourName(api.petAggressiveBehavior),
  info: api.note || ''
});

/** The API's "day", "month" or "none"; anything else as not said. */
export const precisionFromApi = (value: unknown): BirthdayPrecision | undefined =>
  value === 'day' || value === 'month' || value === 'none' ? value : undefined;

/** Whole years from a date of birth to today ("2022-03-12", "2026-10-12" → 4). */
export const ageOn = (dob: string, today: string): number => {
  const [by, bm, bd] = dob.slice(0, 10).split('-').map(Number);
  const [ty, tm, td] = today.slice(0, 10).split('-').map(Number);
  let age = ty - by;
  if (tm < bm || (tm === bm && td < bd)) age -= 1;
  return Math.max(0, age);
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "12 Mar 2022" for a date field's text. */
export const dayText = (day: string): string => {
  const [y, m, d] = day.split('-').map(Number);
  return y && m && d ? `${d} ${MONTHS[m - 1]} ${y}` : '';
};

/**
 * SF 34's green line: "Leo turns 5 on 12 Mar" — the next birthday from today
 * (today's own counts) — and the age now, "4 years old". Nothing without a
 * birthday or before the first.
 */
export const birthdayLine = (
  name: string,
  dob: string,
  today: string,
  monthOnly = false
): { turns: string; age: string } | null => {
  if (monthOnly) return monthBirthdayLine(name, dob, today);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob) || dob > today) return null;
  const [, m, d] = dob.split('-').map(Number);
  const age = ageOn(dob, today);
  const [, tm, td] = today.split('-').map(Number);
  const passed = tm > m || (tm === m && td > d);
  const turnsAge = Number(today.slice(0, 4)) + (passed ? 1 : 0) - Number(dob.slice(0, 4));
  return {
    turns: `${name || 'Your pet'} turns ${turnsAge} on ${d} ${MONTHS[m - 1]}`,
    age: age === 0 ? 'Under a year old.' : `${age} year${age === 1 ? '' : 's'} old.`
  };
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Only the month known: "Leo turns 5 in March" (this month's counts until it
 * ends) and the age now. Nothing before the first birthday or in the future.
 */
const monthBirthdayLine = (name: string, dob: string, today: string): { turns: string; age: string } | null => {
  if (!/^\d{4}-\d{2}/.test(dob) || dob.slice(0, 7) > today.slice(0, 7)) return null;
  const by = Number(dob.slice(0, 4));
  const bm = Number(dob.slice(5, 7));
  const ty = Number(today.slice(0, 4));
  const tm = Number(today.slice(5, 7));
  const turnsAge = ty + (tm > bm ? 1 : 0) - by;
  if (turnsAge < 1) return null;
  const age = ty - by - (tm < bm ? 1 : 0);
  return {
    turns: `${name || 'Your pet'} turns ${turnsAge} in ${MONTH_NAMES[bm - 1]}`,
    age: age <= 0 ? 'Under a year old.' : `${age} year${age === 1 ? '' : 's'} old.`
  };
};
